import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
     Container,
     Paper,
     Title,
     Text,
     Stack,
     Group,
     Button,
     FileButton,
     Select,
     Alert,
     Divider,
     NumberInput,
     Badge,
     Checkbox,
     ThemeIcon,
     TextInput,
     Textarea,
     SimpleGrid,
} from "@mantine/core";
import Loader from "../../components/Loader";
import {
     IconUpload,
     IconCheck,
     IconAlertCircle,
     IconFileText,
     IconId,
     IconHome,
     IconBriefcase,
     IconWallet,
     IconCamera,
     IconUserCheck,
     IconMapPin,
} from "@tabler/icons-react";
import { useToastNotification } from "../../hooks/useToastNotification";
import { uploadFiles } from "../../lib/UploadFile";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../types";
import {
     IKycIdentity,
     IKycProofOfAddress,
     IKycProofOfEmployment,
     IKycFinancialDocument,
     IKycVerificatitonProps,
     IKycAddressInfo,
     IKycNextOfKin,
} from "../../types/Kyc";
import { HttpCodes } from "../../lib/data";
import { useAuth } from "../../contexts/AuthContext";
import { isBvnComplete, isBvnImageVerified } from "../../lib/utils/checkBvnComplete";
import { useNavigate } from "react-router-dom";

interface FileUpload {
     file: File | null;
     fileUrl?: string;
     uploaded: boolean;
     uploading: boolean;
     selected: boolean; // Track if file is selected but not yet uploaded
}

interface KYCDocuments {
     idDocument: {
          type: string;
          ninNumber?: string;
          upload: FileUpload;
     };
     proofOfAddress: {
          type: string;
          upload: FileUpload;
     };
     proofOfEmployment: FileUpload;
     financialDocuments: FileUpload;
}

type AddressFormState = IKycAddressInfo;
type NextOfKinFormState = Omit<IKycNextOfKin, "relationship"> & {
     relationship: IKycNextOfKin["relationship"] | "";
};

const ID_DOCUMENT_OPTIONS = [
     { value: "nin", label: "National Identification Number (NIN)" },
     { value: "drivers-license", label: "Driver's License" },
     { value: "international-passport", label: "International Passport" },
     { value: "voter-card", label: "Voter's Card" },
];

const PROOF_OF_ADDRESS_OPTIONS = [
     { value: "electricity_bill_payment", label: "Electricity Bill Payment" },
     { value: "telephone_payment", label: "Telephone Payment" },
     { value: "estate_dues_service_charges", label: "Estate Dues and Service Charges" },
     { value: "cable_tv_payment", label: "DSTV/GOTV Payment" },
     { value: "waste_disposal_payment", label: "Waste Disposal Payments" },
];

const PROOF_OF_EMPLOYMENT_OPTIONS = [
     { value: "letter_of_employment", label: "Letter of Employment" },
     { value: "company_id_card", label: "Company's ID Card" },
];

const NOK_RELATIONSHIP_OPTIONS = [
     { value: "sibling", label: "Sibling" },
     { value: "offspring", label: "Offspring" },
     { value: "niece", label: "Niece" },
     { value: "nephew", label: "Nephew" },
     { value: "parent", label: "Parent" },
     { value: "grandparent", label: "Grandparent" },
     { value: "spouse", label: "Spouse" },
     { value: "others", label: "Others" },
];

export default function KYC() {
     const { user } = useAuth();
     const navigate = useNavigate();
     const { success, error } = useToastNotification();
     const [submitting, setSubmitting] = useState(false);

     const {
          data: kycStatusResponse,
          isLoading: isLoadingKycStatus,
          reloadData: reloadKycStatus,
     } = usePerformRequest<NonPaginatedResponse<IKycVerificatitonProps>>({
          method: Endpoints.GetKycStatus().method,
          route: Endpoints.GetKycStatus().route,
     });

     const kycStatus = kycStatusResponse?.data;

     // Debug: Log the actual status values from API
     useEffect(() => {
          if (kycStatus) {
               console.log("🔍 KYC Status from API:", {
                    identityStatus: kycStatus.identityStatus,
                    addressStatus: kycStatus.addressStatus,
                    employmentStatus: kycStatus.employmentStatus,
                    financialDocumentStatus: kycStatus.financialDocumentStatus,
                    nextOfKinStatus: kycStatus.nextOfKinStatus,
                    fullResponse: kycStatusResponse,
               });
          }
     }, [kycStatus, kycStatusResponse]);

     // Calculate the first incomplete step based on KYC status
     const getFirstIncompleteStep = useCallback((): number => {
          if (!kycStatus) return 0;

          // Step 0: Identity - find first step that is awaiting or rejected
          if (
               kycStatus.identityStatus === "awaiting" ||
               kycStatus.identityStatus === "rejected"
          )
               return 0;
          if (
               kycStatus.addressStatus === "awaiting" ||
               kycStatus.addressStatus === "rejected"
          )
               return 1;
          if (
               kycStatus.employmentStatus === "awaiting" ||
               kycStatus.employmentStatus === "rejected"
          )
               return 2;
          if (
               kycStatus.financialDocumentStatus === "awaiting" ||
               kycStatus.financialDocumentStatus === "rejected"
          )
               return 3;
          if (
               kycStatus.nextOfKinStatus === "awaiting" ||
               kycStatus.nextOfKinStatus === "rejected"
          )
               return 4;

          // All complete or pending
          return 0;
     }, [kycStatus]);

     const [activeStep, setActiveStep] = useState(() =>
          getFirstIncompleteStep()
     );

     const [documents, setDocuments] = useState<KYCDocuments>({
          idDocument: {
               type: "",
               upload: {
                    file: null,
                    uploaded: false,
                    uploading: false,
                    selected: false,
               },
          },
          proofOfAddress: {
               type: "",
               upload: {
                    file: null,
                    uploaded: false,
                    uploading: false,
                    selected: false,
               },
          },
          proofOfEmployment: {
               file: null,
               uploaded: false,
               uploading: false,
               selected: false,
          },
          financialDocuments: {
               file: null,
               uploaded: false,
               uploading: false,
               selected: false,
          },
     });

     const [proofOfEmploymentType, setProofOfEmploymentType] = useState<string>("");

     const [addressDetails, setAddressDetails] = useState<AddressFormState>({
          addressLine: "",
          nearestLandmarkAndBusStop: "",
          city: "",
          area: "",
          state: "",
          localGovernmentArea: "",
          country: "",
     });

     const [nextOfKinDetails, setNextOfKinDetails] = useState<NextOfKinFormState>({
          firstName: "",
          surname: "",
          emailAddress: "",
          phoneNumber: "",
          relationship: "",
          address: "",
          nearestLandmarkAndBusStop: "",
          city: "",
          state: "",
          localGovernmentArea: "",
          country: "",
     });

     const handleAddressDetailChange = (
          field: keyof AddressFormState,
          value: string
     ) => {
          setAddressDetails((prev) => ({
               ...prev,
               [field]: value,
          }));
     };

     const handleNextOfKinChange = (
          field: keyof NextOfKinFormState,
          value: string
     ) => {
          setNextOfKinDetails((prev) => ({
               ...prev,
               [field]: value,
          }));
     };

     // Confirmation checkboxes state for each step
     const [confirmations, setConfirmations] = useState({
          step0: false,
          step1: false,
          step2: false,
          step3: false,
          step4: false,
     });

     // Update active step when KYC status loads
     useEffect(() => {
          if (kycStatus && activeStep === 0) {
               const firstIncomplete = getFirstIncompleteStep();
               setActiveStep(firstIncomplete);
          }
     }, [kycStatus, activeStep, getFirstIncompleteStep]);

     // Helper functions to check KYC status
     const getStepStatusValue = (
          step: number
     ): "awaiting" | "pending" | "approved" | "rejected" | undefined => {
          if (!kycStatus) return undefined;
          switch (step) {
               case 0:
                    return kycStatus.identityStatus;
               case 1:
                    return kycStatus.addressStatus;
               case 2:
                    return kycStatus.employmentStatus;
               case 3:
                    return kycStatus.financialDocumentStatus;
               case 4:
                    return kycStatus.nextOfKinStatus;
               default:
                    return undefined;
          }
     };

     const isStepSubmitted = (step: number): boolean => {
          const status = getStepStatusValue(step);
          return status === "pending" || status === "approved";
     };

     const isStepVerified = (step: number): boolean => {
          const status = getStepStatusValue(step);
          return status === "approved";
     };

     const canUploadStep = (step: number): boolean => {
          const status = getStepStatusValue(step);
          // Can only upload if awaiting or rejected, AND no document has been uploaded yet
          if (status === "pending" || status === "approved") return false;
          if (status === "rejected") return true;

          // If status is "awaiting", check if a document already exists
          if (status === "awaiting") {
               if (!kycStatus) return true;
               switch (step) {
                    case 0:
                         return !kycStatus.hasIdentity;
                    case 1:
                         return !kycStatus.hasProofOfAddress;
                    case 2:
                         return !kycStatus.hasProofOfEmployment;
                    case 3:
                         return !kycStatus.hasFinancialDocument;
                    case 4:
                         return !kycStatus.hasNextOfKin;
                    default:
                         return true;
               }
          }

          return true;
     };

     const addressFieldLabels: Record<keyof IKycAddressInfo, string> = {
          addressLine: "your residential address",
          nearestLandmarkAndBusStop: "the nearest landmark and bus stop",
          city: "your city",
          area: "your area",
          state: "your state",
          localGovernmentArea: "your local government area",
          country: "your country",
     };

     const nextOfKinFieldLabels: Record<keyof IKycNextOfKin, string> = {
          firstName: "next of kin first name",
          surname: "next of kin surname",
          emailAddress: "next of kin email address",
          phoneNumber: "next of kin phone number",
          relationship: "next of kin relationship",
          address: "next of kin address",
          nearestLandmarkAndBusStop: "next of kin nearest landmark and bus stop",
          city: "next of kin city",
          state: "next of kin state",
          localGovernmentArea: "next of kin local government area",
          country: "next of kin country",
     };

     const validateAddressAndNextOfKin = (): boolean => {
          const missingAddressField = (
               Object.keys(addressFieldLabels) as (keyof IKycAddressInfo)[]
          ).find((field) => !addressDetails[field].trim());

          if (missingAddressField) {
               error(
                    `Please provide ${addressFieldLabels[missingAddressField]}.`
               );
               return false;
          }

          const missingNextField = (
               Object.keys(nextOfKinFieldLabels) as (keyof IKycNextOfKin)[]
          ).find((field) => {
               if (field === "relationship") {
                    return !nextOfKinDetails.relationship;
               }
               return !nextOfKinDetails[field].trim();
          });

          if (missingNextField) {
               error(
                    `Please provide ${nextOfKinFieldLabels[missingNextField]}.`
               );
               return false;
          }

          const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
               nextOfKinDetails.emailAddress.trim()
          );
          if (!emailValid) {
               error("Please enter a valid next of kin email address.");
               return false;
          }

          const phoneDigits = nextOfKinDetails.phoneNumber.replace(/\D/g, "");
          if (phoneDigits.length < 10) {
               error("Next of kin phone number must be at least 10 digits.");
               return false;
          }

          return true;
     };

     const getStepStatus = (
          step: number
     ): "verified" | "submitted" | "awaiting" | "rejected" => {
          const status = getStepStatusValue(step);
          if (status === "approved") return "verified";
          if (status === "pending") return "submitted";
          if (status === "rejected") return "rejected";
          return "awaiting"; // status === "awaiting" or undefined
     };

     const handleFileSelect = (
          field: keyof KYCDocuments,
          file: File | null
     ) => {
          if (!file) return;

          // Just store the file, don't upload yet
          if (field === "idDocument") {
               setDocuments((prev) => ({
                    ...prev,
                    idDocument: {
                         ...prev.idDocument,
                         upload: {
                              ...prev.idDocument.upload,
                              file,
                              selected: true,
                              uploaded: false,
                         },
                    },
               }));
          } else if (field === "proofOfAddress") {
               setDocuments((prev) => ({
                    ...prev,
                    proofOfAddress: {
                         ...prev.proofOfAddress,
                         upload: {
                              ...prev.proofOfAddress.upload,
                              file,
                              selected: true,
                              uploaded: false,
                         },
                    },
               }));
          } else {
               setDocuments((prev) => ({
                    ...prev,
                    [field]: {
                         ...prev[field],
                         file,
                         selected: true,
                         uploaded: false,
                    },
               }));
          }
     };

     const uploadDocument = async (
          field: keyof KYCDocuments
     ): Promise<string | null> => {
          const currentField =
               field === "idDocument"
                    ? documents.idDocument.upload
                    : field === "proofOfAddress"
                    ? documents.proofOfAddress.upload
                    : documents[field];

          if (!currentField || !currentField.file) return null;

          // Mark as uploading
          if (field === "idDocument") {
               setDocuments((prev) => ({
                    ...prev,
                    idDocument: {
                         ...prev.idDocument,
                         upload: {
                              ...prev.idDocument.upload,
                              uploading: true,
                         },
                    },
               }));
          } else if (field === "proofOfAddress") {
               setDocuments((prev) => ({
                    ...prev,
                    proofOfAddress: {
                         ...prev.proofOfAddress,
                         upload: {
                              ...prev.proofOfAddress.upload,
                              uploading: true,
                         },
                    },
               }));
          } else {
               setDocuments((prev) => ({
                    ...prev,
                    [field]: {
                         ...prev[field],
                         uploading: true,
                    },
               }));
          }

          try {
               const uploadedFiles = await uploadFiles([currentField.file], {
                    fieldName: "files",
               });

               if (uploadedFiles && uploadedFiles.length > 0) {
                    const fileUrl = uploadedFiles[0].url;

                    // Update state with uploaded file URL
                    if (field === "idDocument") {
                         setDocuments((prev) => ({
                              ...prev,
                              idDocument: {
                                   ...prev.idDocument,
                                   upload: {
                                        ...prev.idDocument.upload,
                                        fileUrl,
                                        uploading: false,
                                        uploaded: true,
                                   },
                              },
                         }));
                    } else if (field === "proofOfAddress") {
                         setDocuments((prev) => ({
                              ...prev,
                              proofOfAddress: {
                                   ...prev.proofOfAddress,
                                   upload: {
                                        ...prev.proofOfAddress.upload,
                                        fileUrl,
                                        uploading: false,
                                        uploaded: true,
                                   },
                              },
                         }));
                    } else {
                         setDocuments((prev) => ({
                              ...prev,
                              [field]: {
                                   ...prev[field],
                                   fileUrl,
                                   uploading: false,
                                   uploaded: true,
                              },
                         }));
                    }

                    return fileUrl;
               }
               return null;
          } catch (err: any) {
               // Handle upload error
               if (field === "idDocument") {
                    setDocuments((prev) => ({
                         ...prev,
                         idDocument: {
                              ...prev.idDocument,
                              upload: {
                                   ...prev.idDocument.upload,
                                   uploading: false,
                              },
                         },
                    }));
               } else if (field === "proofOfAddress") {
                    setDocuments((prev) => ({
                         ...prev,
                         proofOfAddress: {
                              ...prev.proofOfAddress,
                              upload: {
                                   ...prev.proofOfAddress.upload,
                                   uploading: false,
                              },
                         },
                    }));
               } else {
                    setDocuments((prev) => ({
                         ...prev,
                         [field]: {
                              ...prev[field],
                              uploading: false,
                         },
                    }));
               }
               throw err;
          }
     };


     const isStepComplete = (step: number): boolean => {
          switch (step) {
               case 0:
                    return (
                         documents.idDocument.type !== "" &&
                         // If NIN is selected, NIN number must be provided
                         (documents.idDocument.type !== "nin" ||
                              (documents.idDocument.ninNumber !== undefined &&
                                   documents.idDocument.ninNumber.trim()
                                        .length > 0)) &&
                         documents.idDocument.upload.selected
                    );
               case 1:
                    return (
                         documents.proofOfAddress.type !== "" &&
                         documents.proofOfAddress.upload.selected &&
                         // All address fields must be filled
                         Object.values(addressDetails).every((val) => val.trim() !== "")
                    );
               case 2:
                    return documents.proofOfEmployment.selected;
               case 3:
                    return documents.financialDocuments.selected;
               case 4:
                    // All next of kin fields must be filled
                    return (
                         nextOfKinDetails.firstName.trim() !== "" &&
                         nextOfKinDetails.surname.trim() !== "" &&
                         nextOfKinDetails.emailAddress.trim() !== "" &&
                         nextOfKinDetails.phoneNumber.trim() !== "" &&
                         nextOfKinDetails.relationship !== "" &&
                         nextOfKinDetails.address.trim() !== "" &&
                         nextOfKinDetails.nearestLandmarkAndBusStop.trim() !== "" &&
                         nextOfKinDetails.city.trim() !== "" &&
                         nextOfKinDetails.state.trim() !== "" &&
                         nextOfKinDetails.localGovernmentArea.trim() !== "" &&
                         nextOfKinDetails.country.trim() !== ""
                    );
               default:
                    return false;
          }
     };

     const canProceed = (): boolean => {
          const stepComplete = isStepComplete(activeStep);
          const confirmationKey =
               `step${activeStep}` as keyof typeof confirmations;
          const isConfirmed = confirmations[confirmationKey];
          return stepComplete && isConfirmed;
     };

     const findNextIncompleteStep = (currentStep: number): number => {
          // Find the next step that is not submitted or verified
          for (let i = currentStep + 1; i < uploadSections.length; i++) {
               if (!isStepSubmitted(i)) {
                    return i;
               }
          }
          // If all subsequent steps are complete, stay on current or return to first incomplete
          return getFirstIncompleteStep();
     };

     const handleNextStep = async () => {
          if (!canProceed()) {
               error("Please complete all required fields");
               return;
          }

          // Check if this step can be uploaded (not pending or approved)
          if (!canUploadStep(activeStep)) {
               const status = getStepStatusValue(activeStep);
               if (status === "pending") {
                    error(
                         "This document is already pending review. Please wait for admin approval."
                    );
               } else if (status === "approved") {
                    error(
                         "This document has already been approved. You cannot upload a new one."
                    );
               }
               return;
          }

          // Check if BVN is complete before allowing KYC submission
          if (!isBvnComplete(user)) {
               error(
                    "Please complete your BVN profile verification before submitting KYC documents."
               );
               navigate("/dashboard/profile");
               return;
          }

          setSubmitting(true);

          try {
               // If on identity document step (step 0), upload and submit to API
               if (activeStep === 0) {
                    // Upload the file first
                    const fileUrl = await uploadDocument("idDocument");

                    if (!fileUrl) {
                         error("Failed to upload document");
                         setSubmitting(false);
                         return;
                    }

                    // Map the document type to API format
                    const documentTypeMap: Record<string, string> = {
                         nin: "NIN",
                         "drivers-license": "drivers_license",
                         "international-passport": "Passport",
                         "voter-card": "voters_card",
                    };

                    const payload: {
                         documentType: string;
                         file: string;
                         value?: string;
                    } = {
                         documentType:
                              documentTypeMap[documents.idDocument.type] ||
                              documents.idDocument.type.toUpperCase(),
                         file: fileUrl,
                    };

                    // Only include value if documentType is NIN
                    if (
                         documents.idDocument.type === "nin" &&
                         documents.idDocument.ninNumber
                    ) {
                         payload.value = documents.idDocument.ninNumber;
                    }

                    const response = await PerformRequest<
                         NonPaginatedResponse<IKycIdentity>
                    >({
                         method: Endpoints.GetSubmitIdentityDocument().method,
                         route: Endpoints.GetSubmitIdentityDocument().route,
                         data: payload,
                    });

                    if (
                         response &&
                         (response.status === HttpCodes.Success ||
                              response.status === HttpCodes.Created)
                    ) {
                         success("Identity document submitted successfully!");
                         await reloadKycStatus();
                         const nextStep = findNextIncompleteStep(activeStep);
                         setActiveStep(nextStep);
                    } else {
                         error(
                              response?.message ||
                                   "Failed to submit identity document"
                         );
                    }
               } else if (activeStep === 1) {
                    if (!documents.proofOfAddress.type) {
                         error("Please select a proof of address document type.");
                         setSubmitting(false);
                         return;
                    }

                    // Validate only address fields
                    const missingAddressField = (
                         Object.keys(addressFieldLabels) as (keyof IKycAddressInfo)[]
                    ).find((field) => !addressDetails[field].trim());

                    if (missingAddressField) {
                         error(
                              `Please provide ${addressFieldLabels[missingAddressField]}.`
                         );
                         setSubmitting(false);
                         return;
                    }

                    // Proof of Address - upload and submit to API
                    const fileUrl = await uploadDocument("proofOfAddress");

                    if (!fileUrl) {
                         error("Failed to upload document");
                         setSubmitting(false);
                         return;
                    }

                    const sanitizedAddressInfo: IKycAddressInfo = {
                         addressLine: addressDetails.addressLine.trim(),
                         nearestLandmarkAndBusStop:
                              addressDetails.nearestLandmarkAndBusStop.trim(),
                         city: addressDetails.city.trim(),
                         area: addressDetails.area.trim(),
                         state: addressDetails.state.trim(),
                         localGovernmentArea:
                              addressDetails.localGovernmentArea.trim(),
                         country: addressDetails.country.trim(),
                    };

                    const payload = {
                         documentType: documents.proofOfAddress.type,
                         file: fileUrl,
                         addressInfo: sanitizedAddressInfo,
                    };

                    const response = await PerformRequest<
                         NonPaginatedResponse<IKycProofOfAddress>
                    >({
                         method: Endpoints.GetSubmitProofOfAddress().method,
                         route: Endpoints.GetSubmitProofOfAddress().route,
                         data: payload,
                    });

                    if (
                         response &&
                         (response.status === HttpCodes.Success ||
                              response.status === HttpCodes.Created)
                    ) {
                         success("Proof of address submitted successfully!");
                         await reloadKycStatus();
                         const nextStep = findNextIncompleteStep(activeStep);
                         setActiveStep(nextStep);
                    } else {
                         error(
                              response?.message ||
                                   "Failed to submit proof of address"
                         );
                    }
               } else if (activeStep === 2) {
                    // Proof of Employment - upload and submit to API
                    if (!proofOfEmploymentType) {
                         error("Please select the type of proof of employment document.");
                         setSubmitting(false);
                         return;
                    }

                    const fileUrl = await uploadDocument("proofOfEmployment");

                    if (!fileUrl) {
                         error("Failed to upload document");
                         setSubmitting(false);
                         return;
                    }

                    const payload = {
                         documentType: proofOfEmploymentType,
                         file: fileUrl,
                    };

                    const response = await PerformRequest<
                         NonPaginatedResponse<IKycProofOfEmployment>
                    >({
                         method: Endpoints.GetSubmitProofOfEmployment().method,
                         route: Endpoints.GetSubmitProofOfEmployment().route,
                         data: payload,
                    });

                    if (
                         response &&
                         (response.status === HttpCodes.Success ||
                              response.status === HttpCodes.Created)
                    ) {
                         success("Proof of employment submitted successfully!");
                         await reloadKycStatus();
                         const nextStep = findNextIncompleteStep(activeStep);
                         setActiveStep(nextStep);
                    } else {
                         error(
                              response?.message ||
                                   "Failed to submit proof of employment"
                         );
                    }
               } else if (activeStep === 3) {
                    // Financial Documents - upload and submit to API
                    const fileUrl = await uploadDocument("financialDocuments");

                    if (!fileUrl) {
                         error("Failed to upload document");
                         setSubmitting(false);
                         return;
                    }

                    const payload = {
                         documentType: "bank_statement",
                         file: fileUrl,
                    };

                    const response = await PerformRequest<
                         NonPaginatedResponse<IKycFinancialDocument>
                    >({
                         method: Endpoints.GetSubmitFinancialDocuments().method,
                         route: Endpoints.GetSubmitFinancialDocuments().route,
                         data: payload,
                    });

                    if (
                         response &&
                         (response.status === HttpCodes.Success ||
                              response.status === HttpCodes.Created)
                    ) {
                         success("Financial document submitted successfully!");
                         // Small delay to ensure database update completes
                         await new Promise((resolve) =>
                              setTimeout(resolve, 500)
                         );
                         await reloadKycStatus();
                         const nextStep = findNextIncompleteStep(activeStep);
                         setActiveStep(nextStep);
                    } else {
                         error(
                              response?.message ||
                                   "Failed to submit financial document"
                         );
                    }
               } else if (activeStep === 4) {
                    // Next of Kin - submit to API
                    // Validate next of kin fields
                    const missingNextField = (
                         Object.keys(nextOfKinFieldLabels) as (keyof IKycNextOfKin)[]
                    ).find((field) => {
                         if (field === "relationship") {
                              return !nextOfKinDetails.relationship;
                         }
                         return !nextOfKinDetails[field].trim();
                    });

                    if (missingNextField) {
                         error(
                              `Please provide ${nextOfKinFieldLabels[missingNextField]}.`
                         );
                         setSubmitting(false);
                         return;
                    }

                    const sanitizedNextOfKin: IKycNextOfKin = {
                         firstName: nextOfKinDetails.firstName.trim(),
                         surname: nextOfKinDetails.surname.trim(),
                         emailAddress: nextOfKinDetails.emailAddress.trim(),
                         phoneNumber: nextOfKinDetails.phoneNumber
                              .replace(/\D/g, "")
                              .trim(),
                         relationship:
                              nextOfKinDetails
                                   .relationship as IKycNextOfKin["relationship"],
                         address: nextOfKinDetails.address.trim(),
                         nearestLandmarkAndBusStop:
                              nextOfKinDetails.nearestLandmarkAndBusStop.trim(),
                         city: nextOfKinDetails.city.trim(),
                         state: nextOfKinDetails.state.trim(),
                         localGovernmentArea:
                              nextOfKinDetails.localGovernmentArea.trim(),
                         country: nextOfKinDetails.country.trim(),
                    };

                    const payload = {
                         nextOfKin: sanitizedNextOfKin,
                    };

                    const response = await PerformRequest<
                         NonPaginatedResponse<any>
                    >({
                         method: Endpoints.GetSubmitNextOfKin().method,
                         route: Endpoints.GetSubmitNextOfKin().route,
                         data: payload,
                    });

                    if (
                         response &&
                         (response.status === HttpCodes.Success ||
                              response.status === HttpCodes.Created)
                    ) {
                         success("Next of kin submitted successfully!");
                         await reloadKycStatus();
                         const nextStep = findNextIncompleteStep(activeStep);
                         setActiveStep(nextStep);
                    } else {
                         error(
                              response?.message ||
                                   "Failed to submit next of kin"
                         );
                    }
               }
          } catch (err: any) {
               error(`Failed: ${err.message || "Unknown error"}`);
          } finally {
               setSubmitting(false);
          }
     };

     const uploadSections = [
          {
               title: "ID Document",
               description:
                    "Upload a valid government-issued identification document",
               icon: IconId,
               field: "idDocument" as keyof KYCDocuments,
               requiresSelection: true,
               isIdDocument: true,
          },
          {
               title: "Proof of Address",
               description:
                    "Upload a utility bill or other document showing your address",
               icon: IconHome,
               field: "proofOfAddress" as keyof KYCDocuments,
               requiresSelection: true,
               isProofOfAddress: true,
          },
          {
               title: "Proof of Employment",
               description:
                    "Upload employment letter or company ID card",
               icon: IconBriefcase,
               field: "proofOfEmployment" as keyof KYCDocuments,
               requiresSelection: true,
               isIdDocument: false,
               isProofOfAddress: false,
          },
          {
               title: "Financial Documents",
               description: "Upload 6 months bank statement",
               icon: IconWallet,
               field: "financialDocuments" as keyof KYCDocuments,
               requiresSelection: false,
               isIdDocument: false,
               isProofOfAddress: false,
          },
          {
               title: "Next of Kin",
               description: "Provide your next of kin information and address",
               icon: IconUserCheck,
               field: "nextOfKin" as any,
               requiresSelection: false,
               isIdDocument: false,
               isProofOfAddress: false,
               isNextOfKin: true,
          },
     ];

     const verifiedCount = uploadSections.filter((_, index) =>
          isStepVerified(index)
     ).length;
     const submittedCount = uploadSections.filter(
          (_, index) => isStepSubmitted(index) && !isStepVerified(index)
     ).length;
     const totalSteps = uploadSections.length;

     // Show loading state while fetching KYC status
     if (isLoadingKycStatus) {
          return (
               <Container size="lg" py="xl">
                    <Stack
                         align="center"
                         gap="xl"
                         style={{
                              minHeight: "400px",
                              justifyContent: "center",
                         }}
                    >
                         <Loader size="xl" />
                         <Text size="lg" c="dimmed">
                              Loading your KYC status...
                         </Text>
                    </Stack>
               </Container>
          );
     }

     // Show BVN completion requirement if BVN is not complete
     if (user && !isBvnComplete(user)) {
          return (
               <Container size="lg" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background: "white",
                              border: "2px solid #f59e0b",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="yellow"
                                   variant="light"
                              >
                                   <IconAlertCircle size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#92400e" }}
                                   >
                                        BVN Verification Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="xl">
                                        You must complete your BVN profile
                                        verification before you can submit KYC
                                        documents. This helps us verify your
                                        identity and process your KYC
                                        verification securely.
                                   </Text>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/profile")
                                        }
                                        style={{
                                             background: "#679369",
                                        }}
                                        leftSection={<IconId size={20} />}
                                   >
                                        Complete BVN Verification
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

    // Show BVN image verification requirement if BVN image is not verified
    if (user && isBvnComplete(user) && !isBvnImageVerified(user)) {
          return (
               <Container size="lg" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                              border: "2px solid #fcd34d",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="yellow"
                                   variant="light"
                              >
                                   <IconAlertCircle size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#78350f" }}
                                   >
                                        BVN Image Verification Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="md">
                                        You must complete your BVN image verification by capturing a selfie 
                                        that matches your BVN photo before you can submit KYC documents.
                                   </Text>
                                   <Alert 
                                        color="yellow" 
                                        radius="md"
                                        mb="xl"
                                        style={{ 
                                             border: "1px solid #fcd34d",
                                             background: "white",
                                             textAlign: "left"
                                        }}
                                   >
                                        <Group gap={8} mb="xs">
                                             <IconCamera size={16} style={{ color: "#78350f" }} />
                                             <Text size="sm" fw={600} style={{ color: "#78350f" }}>
                                                  Selfie Requirements:
                                             </Text>
                                        </Group>
                                        <Stack gap={4}>
                                             <Group gap={8}>
                                                  <IconCheck size={14} style={{ color: "#679369" }} />
                                                  <Text size="sm">Your face is clearly visible and well-lit</Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck size={14} style={{ color: "#679369" }} />
                                                  <Text size="sm">No caps, hats, or head coverings</Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck size={14} style={{ color: "#679369" }} />
                                                  <Text size="sm">No glasses</Text>
                                             </Group>
                                             <Group gap={8}>
                                                  <IconCheck size={14} style={{ color: "#679369" }} />
                                                  <Text size="sm">Good lighting conditions</Text>
                                             </Group>
                                        </Stack>
                                   </Alert>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/profile")
                                        }
                                        style={{
                                             background: "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                             fontWeight: 600,
                                        }}
                                        leftSection={<IconId size={20} />}
                                   >
                                        Complete BVN Image Verification
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     // Require residential address on profile before allowing KYC
     if (user && !user.address) {
          return (
               <Container size="lg" py="xl">
                    <Paper
                         shadow="md"
                         p="xl"
                         radius="lg"
                         style={{
                              background:
                                   "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                              border: "2px solid #93c5fd",
                         }}
                    >
                         <Stack gap="lg" align="center">
                              <ThemeIcon
                                   size={80}
                                   radius="md"
                                   color="blue"
                                   variant="light"
                              >
                                   <IconMapPin size={40} />
                              </ThemeIcon>
                              <div style={{ textAlign: "center" }}>
                                   <Title
                                        order={2}
                                        fw={700}
                                        mb="md"
                                        style={{ color: "#1d4ed8" }}
                                   >
                                        Residential Address Required
                                   </Title>
                                   <Text size="lg" c="dimmed" mb="xl">
                                        You must complete your residential address
                                        on your profile before you can submit KYC
                                        documents. This helps us verify your
                                        identity and eligibility for loans.
                                   </Text>
                                   <Button
                                        size="lg"
                                        onClick={() =>
                                             navigate("/dashboard/profile")
                                        }
                                        style={{
                                             background:
                                                  "linear-gradient(135deg, #679369 0%, #558B2F 100%)",
                                        }}
                                        leftSection={<IconMapPin size={20} />}
                                   >
                                        Complete Address Information
                                   </Button>
                              </div>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     return (
          <Container size="lg" py="xl">
               <Stack gap="xl">
                    {/* Header */}
                    <motion.div
                         initial={{ opacity: 0, y: -20 }}
                         animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.5 }}
                    >
                         <Title
                              order={1}
                              size="3rem"
                              fw={800}
                              className="mb-4"
                              style={{ color: "#679369" }}
                         >
                              Complete Your KYC
                         </Title>
                         <Text size="lg" c="dimmed">
                              Please provide the following documents to verify
                              your identity. All fields are required.
                         </Text>

                         {/* Overall KYC Status */}
                         {kycStatus?.isVerified && (
                              <Alert
                                   icon={<IconCheck size={16} />}
                                   title="KYC Verified"
                                   color="green"
                                   radius="md"
                                   mt="md"
                              >
                                   Your KYC has been verified. You can now
                                   access all features.
                              </Alert>
                         )}
                    </motion.div>

                    {/* Progress Indicator */}
                    <Paper p="md" radius="lg" style={{ background: "#F9F9F9" }}>
                         <Stack gap="md">
                              <Group justify="space-between">
                                   <Group gap="xl">
                                        <Group gap="xs">
                                             <div
                                                  style={{
                                                       width: "12px",
                                                       height: "12px",
                                                       borderRadius: "50%",
                                                       background: "#40C057",
                                                  }}
                                             />
                                             <Text size="sm" fw={500}>
                                                  Verified: {verifiedCount}
                                             </Text>
                                        </Group>
                                        <Group gap="xs">
                                             <div
                                                  style={{
                                                       width: "12px",
                                                       height: "12px",
                                                       borderRadius: "50%",
                                                       background: "#228BE6",
                                                  }}
                                             />
                                             <Text size="sm" fw={500}>
                                                  Submitted: {submittedCount}
                                             </Text>
                                        </Group>
                                        <Group gap="xs">
                                             <div
                                                  style={{
                                                       width: "12px",
                                                       height: "12px",
                                                       borderRadius: "50%",
                                                       background: "#E0E0E0",
                                                  }}
                                             />
                                             <Text size="sm" fw={500}>
                                                  Pending:{" "}
                                                  {totalSteps -
                                                       verifiedCount -
                                                       submittedCount}
                                             </Text>
                                        </Group>
                                   </Group>
                              </Group>

                              {/* Multi-segment Progress Bar */}
                              <div
                                   style={{
                                        display: "flex",
                                        width: "100%",
                                        height: "12px",
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        background: "#E0E0E0",
                                   }}
                              >
                                   {/* Verified segment */}
                                   <div
                                        style={{
                                             width: `${
                                                  (verifiedCount / totalSteps) *
                                                  100
                                             }%`,
                                             background: "#40C057",
                                             transition: "width 0.3s ease",
                                        }}
                                   />
                                   {/* Submitted segment */}
                                   <div
                                        style={{
                                             width: `${
                                                  (submittedCount /
                                                       totalSteps) *
                                                  100
                                             }%`,
                                             background: "#228BE6",
                                             transition: "width 0.3s ease",
                                        }}
                                   />
                                   {/* Pending segment takes up remaining space automatically */}
                              </div>
                         </Stack>
                    </Paper>

                    {/* Stacked Sections - only active section is interactive; others are blurred */}
                    <Stack gap="xl">
                         {uploadSections.map((section, index) => {
                              const IconComponent = section.icon;
                              const isActive = activeStep === index;
                              const stepStatus = getStepStatus(index);
                              const isSubmittedOrVerified =
                                   isStepSubmitted(index);
                              // Get the actual file upload field (nested for idDocument and proofOfAddress)
                              const getCurrentField = (): FileUpload | null => {
                                   if (section.field === "idDocument") {
                                        return documents.idDocument.upload;
                                   }
                                   if (section.field === "proofOfAddress") {
                                        return documents.proofOfAddress.upload;
                                   }
                                   if (section.field === "nextOfKin") {
                                        return null; // Next of Kin doesn't have file upload
                                   }
                                   const field = documents[section.field as keyof KYCDocuments];
                                   // For proofOfEmployment and financialDocuments, field is FileUpload directly
                                   if (section.field === "proofOfEmployment" || section.field === "financialDocuments") {
                                        return field as FileUpload;
                                   }
                                   return null;
                              };
                              const currentField = getCurrentField();

                              return (
                                   <motion.div
                                        key={section.field}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3 }}
                                   >
                                        <Paper
                                             shadow="sm"
                                             p="xl"
                                             radius="lg"
                                             style={{
                                                  border: `2px solid ${
                                                       stepStatus === "verified"
                                                            ? "#40C057"
                                                            : stepStatus ===
                                                              "submitted"
                                                            ? "#228BE6"
                                                            : "#E0E0E0"
                                                  }`,
                                                  background: "white",
                                                  filter: isActive
                                                       ? "none"
                                                       : "blur(1.5px)",
                                                  opacity: isActive ? 1 : 0.6,
                                                  pointerEvents:
                                                       isActive &&
                                                       canUploadStep(index)
                                                            ? "auto"
                                                            : "none",
                                             }}
                                        >
                                             <Stack gap="lg">
                                                  {/* Section Header */}
                                                  <Group
                                                       gap="md"
                                                       justify="space-between"
                                                  >
                                                       <Group gap="md">
                                                            <div
                                                                 style={{
                                                                      background:
                                                                           stepStatus ===
                                                                           "verified"
                                                                                ? "#40C057"
                                                                                : stepStatus ===
                                                                                  "submitted"
                                                                                ? "#228BE6"
                                                                                : "#679369",
                                                                      borderRadius:
                                                                           "12px",
                                                                      padding: "12px",
                                                                      display: "flex",
                                                                      alignItems:
                                                                           "center",
                                                                      justifyContent:
                                                                           "center",
                                                                 }}
                                                            >
                                                                 <IconComponent
                                                                      size={24}
                                                                      style={{
                                                                           color: "white",
                                                                      }}
                                                                 />
                                                            </div>
                                                            <div>
                                                                 <Group gap="xs">
                                                                      <Title
                                                                           order={
                                                                                3
                                                                           }
                                                                           style={{
                                                                                color: "#2C2C2C",
                                                                                margin: 0,
                                                                           }}
                                                                      >
                                                                           {
                                                                                section.title
                                                                           }
                                                                      </Title>
                                                                 </Group>
                                                                 <Text
                                                                      size="sm"
                                                                      c="dimmed"
                                                                      mt="xs"
                                                                 >
                                                                      {
                                                                           section.description
                                                                      }
                                                                 </Text>
                                                            </div>
                                                       </Group>

                                                       {/* Status Badge */}
                                                       {stepStatus ===
                                                            "verified" && (
                                                            <Badge
                                                                 color="green"
                                                                 size="lg"
                                                                 variant="light"
                                                            >
                                                                 <Group gap={4}>
                                                                      <IconCheck
                                                                           size={
                                                                                14
                                                                           }
                                                                      />
                                                                      <span>
                                                                           Verified
                                                                      </span>
                                                                 </Group>
                                                            </Badge>
                                                       )}
                                                       {stepStatus ===
                                                            "submitted" && (
                                                            <Badge
                                                                 color="blue"
                                                                 size="lg"
                                                                 variant="light"
                                                            >
                                                                 <Group gap={4}>
                                                                      <IconAlertCircle
                                                                           size={
                                                                                14
                                                                           }
                                                                      />
                                                                      <span>
                                                                           Under
                                                                           Review
                                                                      </span>
                                                                 </Group>
                                                            </Badge>
                                                       )}
                                                       {stepStatus ===
                                                            "rejected" && (
                                                            <Badge
                                                                 color="red"
                                                                 size="lg"
                                                                 variant="light"
                                                            >
                                                                 <Group gap={4}>
                                                                      <IconAlertCircle
                                                                           size={
                                                                                14
                                                                           }
                                                                      />
                                                                      <span>
                                                                           Rejected
                                                                      </span>
                                                                 </Group>
                                                            </Badge>
                                                       )}
                                                       {/* Debug: Show actual status value */}
                                                  </Group>

                                                  <Divider />

                                                  {/* Show message if submitted or verified */}
                                                  {isSubmittedOrVerified ? (
                                                       <Alert
                                                            icon={
                                                                 stepStatus ===
                                                                 "verified" ? (
                                                                      <IconCheck
                                                                           size={
                                                                                16
                                                                           }
                                                                      />
                                                                 ) : (
                                                                      <IconAlertCircle
                                                                           size={
                                                                                16
                                                                           }
                                                                      />
                                                                 )
                                                            }
                                                            title={
                                                                 stepStatus ===
                                                                 "verified"
                                                                      ? "Document Verified"
                                                                      : "Document Under Review"
                                                            }
                                                            color={
                                                                 stepStatus ===
                                                                 "verified"
                                                                      ? "green"
                                                                      : "blue"
                                                            }
                                                            radius="md"
                                                       >
                                                            <Text size="sm">
                                                                 {stepStatus ===
                                                                 "verified"
                                                                      ? "Your document has been verified and approved. You cannot modify it."
                                                                      : "Your document has been submitted and is currently under review. You cannot modify it while it is being reviewed."}
                                                            </Text>
                                                       </Alert>
                                                  ) : (
                                                       <>
                                                            {/* ID Document Type Selection */}
                                                            {section.requiresSelection &&
                                                                 section.isIdDocument && (
                                                                      <Select
                                                                           label="Document Type *"
                                                                           placeholder="Select document type"
                                                                           data={
                                                                                ID_DOCUMENT_OPTIONS
                                                                           }
                                                                           value={
                                                                                documents
                                                                                     .idDocument
                                                                                     .type
                                                                           }
                                                                           onChange={(
                                                                                value
                                                                           ) =>
                                                                                setDocuments(
                                                                                     (
                                                                                          prev
                                                                                     ) => ({
                                                                                          ...prev,
                                                                                          idDocument:
                                                                                               {
                                                                                                    ...prev.idDocument,
                                                                                                    type:
                                                                                                         value ||
                                                                                                         "",
                                                                                               },
                                                                                     })
                                                                                )
                                                                           }
                                                                           required
                                                                           size="md"
                                                                      />
                                                                 )}

                                                            {/* Proof of Address Type Selection */}
                                                            {section.requiresSelection &&
                                                                 section.isProofOfAddress && (
                                                                      <Select
                                                                           label="Document Type *"
                                                                           placeholder="Select document type"
                                                                           data={
                                                                                PROOF_OF_ADDRESS_OPTIONS
                                                                           }
                                                                           value={
                                                                                documents
                                                                                     .proofOfAddress
                                                                                     .type
                                                                           }
                                                                           onChange={(
                                                                                value
                                                                           ) =>
                                                                                setDocuments(
                                                                                     (
                                                                                          prev
                                                                                     ) => ({
                                                                                          ...prev,
                                                                                          proofOfAddress:
                                                                                               {
                                                                                                    ...prev.proofOfAddress,
                                                                                                    type:
                                                                                                         value ||
                                                                                                         "",
                                                                                               },
                                                                                     })
                                                                                )
                                                                           }
                                                                           required
                                                                           size="md"
                                                                      />
                                                                 )}

                                                            {/* Proof of Employment Type Selection */}
                                                            {section.requiresSelection &&
                                                                 !section.isIdDocument &&
                                                                 !section.isProofOfAddress &&
                                                                 section.field === "proofOfEmployment" && (
                                                                      <Select
                                                                           label="Document Type *"
                                                                           placeholder="Select document type"
                                                                           data={PROOF_OF_EMPLOYMENT_OPTIONS}
                                                                           value={proofOfEmploymentType}
                                                                           onChange={(value) =>
                                                                                setProofOfEmploymentType(value || "")
                                                                           }
                                                                           required
                                                                           size="md"
                                                                      />
                                                                 )}

                                                            {section.isProofOfAddress && (
                                                                 <Stack gap="md">
                                                                      <Divider
                                                                           label="Residential Address"
                                                                           labelPosition="center"
                                                                      />
                                                                      <Textarea
                                                                           label="Address *"
                                                                           placeholder="Enter your full residential address"
                                                                           autosize
                                                                           minRows={2}
                                                                           value={addressDetails.addressLine}
                                                                           onChange={(event) =>
                                                                                handleAddressDetailChange(
                                                                                     "addressLine",
                                                                                     event
                                                                                          .currentTarget
                                                                                          .value
                                                                                )
                                                                           }
                                                                           required
                                                                      />
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="Nearest Landmark and Bus Stop *"
                                                                                placeholder="e.g. Central Park Bus Stop"
                                                                                value={
                                                                                     addressDetails.nearestLandmarkAndBusStop
                                                                                }
                                                                                onChange={(event) =>
                                                                                     handleAddressDetailChange(
                                                                                          "nearestLandmarkAndBusStop",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Area *"
                                                                                placeholder="Enter your area"
                                                                                value={addressDetails.area}
                                                                                onChange={(event) =>
                                                                                     handleAddressDetailChange(
                                                                                          "area",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2, md: 3 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="City *"
                                                                                placeholder="Enter your city"
                                                                                value={addressDetails.city}
                                                                                onChange={(event) =>
                                                                                     handleAddressDetailChange(
                                                                                          "city",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="State *"
                                                                                placeholder="Enter your state"
                                                                                value={addressDetails.state}
                                                                                onChange={(event) =>
                                                                                     handleAddressDetailChange(
                                                                                          "state",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Country *"
                                                                                placeholder="Enter your country"
                                                                                value={addressDetails.country}
                                                                                onChange={(event) =>
                                                                                     handleAddressDetailChange(
                                                                                          "country",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                      <TextInput
                                                                           label="Local Government Area *"
                                                                           placeholder="Enter your local government area"
                                                                           value={
                                                                                addressDetails.localGovernmentArea
                                                                           }
                                                                           onChange={(event) =>
                                                                                handleAddressDetailChange(
                                                                                     "localGovernmentArea",
                                                                                     event
                                                                                          .currentTarget
                                                                                          .value
                                                                                )
                                                                           }
                                                                           required
                                                                      />
                                                                 </Stack>
                                                            )}

                                                            {/* Next of Kin Form - Step 4 */}
                                                            {section.isNextOfKin && (
                                                                 <Stack gap="md">
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="First Name *"
                                                                                placeholder="Enter first name"
                                                                                value={nextOfKinDetails.firstName}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "firstName",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Surname *"
                                                                                placeholder="Enter surname"
                                                                                value={nextOfKinDetails.surname}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "surname",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="Email Address *"
                                                                                placeholder="Enter email address"
                                                                                type="email"
                                                                                value={
                                                                                     nextOfKinDetails.emailAddress
                                                                                }
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "emailAddress",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Phone Number *"
                                                                                placeholder="Enter phone number"
                                                                                value={nextOfKinDetails.phoneNumber}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "phoneNumber",
                                                                                          event.currentTarget.value.replace(
                                                                                               /\D/g,
                                                                                               ""
                                                                                          )
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                      <Select
                                                                           label="Relationship *"
                                                                           placeholder="Select relationship"
                                                                           data={NOK_RELATIONSHIP_OPTIONS}
                                                                           value={nextOfKinDetails.relationship}
                                                                           onChange={(value) =>
                                                                                handleNextOfKinChange(
                                                                                     "relationship",
                                                                                     value || ""
                                                                                )
                                                                           }
                                                                           required
                                                                      />
                                                                      <Textarea
                                                                           label="Next of Kin Address *"
                                                                           placeholder="Enter full address"
                                                                           autosize
                                                                           minRows={2}
                                                                           value={nextOfKinDetails.address}
                                                                           onChange={(event) =>
                                                                                handleNextOfKinChange(
                                                                                     "address",
                                                                                     event.currentTarget.value
                                                                                )
                                                                           }
                                                                           required
                                                                      />
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="Nearest Landmark and Bus Stop *"
                                                                                placeholder="e.g. City Mall Bus Stop"
                                                                                value={
                                                                                     nextOfKinDetails.nearestLandmarkAndBusStop
                                                                                }
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "nearestLandmarkAndBusStop",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="City *"
                                                                                placeholder="Enter city"
                                                                                value={nextOfKinDetails.city}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "city",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                      <SimpleGrid
                                                                           cols={{ base: 1, sm: 2, md: 3 }}
                                                                           spacing="md"
                                                                      >
                                                                           <TextInput
                                                                                label="State *"
                                                                                placeholder="Enter state"
                                                                                value={nextOfKinDetails.state}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "state",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Local Government Area *"
                                                                                placeholder="Enter LGA"
                                                                                value={
                                                                                     nextOfKinDetails.localGovernmentArea
                                                                                }
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "localGovernmentArea",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                           <TextInput
                                                                                label="Country *"
                                                                                placeholder="Enter country"
                                                                                value={nextOfKinDetails.country}
                                                                                onChange={(event) =>
                                                                                     handleNextOfKinChange(
                                                                                          "country",
                                                                                          event
                                                                                               .currentTarget
                                                                                               .value
                                                                                     )
                                                                                }
                                                                                required
                                                                           />
                                                                      </SimpleGrid>
                                                                 </Stack>
                                                            )}

                                                            {/* NIN Number - required only when NIN is selected */}
                                                            {section.requiresSelection &&
                                                                 section.isIdDocument &&
                                                                 documents
                                                                      .idDocument
                                                                      .type ===
                                                                      "nin" && (
                                                                      <NumberInput
                                                                           label="NIN Number *"
                                                                           placeholder="Enter your 11-digit NIN"
                                                                           value={
                                                                                documents
                                                                                     .idDocument
                                                                                     .ninNumber ||
                                                                                ""
                                                                           }
                                                                           onChange={(
                                                                                val
                                                                           ) => {
                                                                                const str =
                                                                                     typeof val ===
                                                                                     "number"
                                                                                          ? String(
                                                                                                 val
                                                                                            )
                                                                                          : val ||
                                                                                            "";
                                                                                const numeric =
                                                                                     str
                                                                                          .replace(
                                                                                               /\D/g,
                                                                                               ""
                                                                                          )
                                                                                          .slice(
                                                                                               0,
                                                                                               11
                                                                                          );
                                                                                setDocuments(
                                                                                     (
                                                                                          prev
                                                                                     ) => ({
                                                                                          ...prev,
                                                                                          idDocument:
                                                                                               {
                                                                                                    ...prev.idDocument,
                                                                                                    ninNumber:
                                                                                                         numeric,
                                                                                               },
                                                                                     })
                                                                                );
                                                                           }}
                                                                           inputMode="numeric"
                                                                           hideControls
                                                                           maxLength={
                                                                                11
                                                                           }
                                                                           min={
                                                                                0
                                                                           }
                                                                           required
                                                                           size="md"
                                                                      />
                                                                 )}

                                                            {/* Upload Area */}
                                                            {!section.isNextOfKin && (!section.requiresSelection ||
                                                                 (section.isIdDocument &&
                                                                      documents
                                                                           .idDocument
                                                                           .type !==
                                                                           "") ||
                                                                 (section.isProofOfAddress &&
                                                                      documents
                                                                           .proofOfAddress
                                                                           .type !==
                                                                           "")) && (
                                                                 <div>
                                                                      <Text
                                                                           size="sm"
                                                                           fw={
                                                                                600
                                                                           }
                                                                           mb="xs"
                                                                           style={{
                                                                                color: "#2C2C2C",
                                                                           }}
                                                                      >
                                                                           Upload
                                                                           Document
                                                                           *
                                                                      </Text>
                                                                      {currentField && (currentField.selected ||
                                                                      currentField.uploaded) ? (
                                                                           <Alert
                                                                                icon={
                                                                                     <IconCheck
                                                                                          size={
                                                                                               16
                                                                                          }
                                                                                     />
                                                                                }
                                                                                title={
                                                                                     currentField.uploaded
                                                                                          ? "Document Uploaded"
                                                                                          : "Document Selected"
                                                                                }
                                                                                color={
                                                                                     currentField.uploaded
                                                                                          ? "green"
                                                                                          : "blue"
                                                                                }
                                                                                radius="md"
                                                                           >
                                                                                <Group justify="space-between">
                                                                                     <Text size="sm">
                                                                                          {currentField?.file
                                                                                               ?.name ||
                                                                                               "Document selected"}
                                                                                     </Text>
                                                                                     <FileButton
                                                                                          onChange={(
                                                                                               file
                                                                                          ) =>
                                                                                               handleFileSelect(
                                                                                                    section.field,
                                                                                                    file
                                                                                               )
                                                                                          }
                                                                                          accept="image/*,.pdf"
                                                                                     >
                                                                                          {(
                                                                                               props
                                                                                          ) => (
                                                                                               <Button
                                                                                                    {...props}
                                                                                                    variant="subtle"
                                                                                                    size="xs"
                                                                                                    style={{
                                                                                                         color: "#679369",
                                                                                                    }}
                                                                                               >
                                                                                                    Replace
                                                                                               </Button>
                                                                                          )}
                                                                                     </FileButton>
                                                                                </Group>
                                                                           </Alert>
                                                                      ) : (
                                                                           <FileButton
                                                                                onChange={(
                                                                                     file
                                                                                ) =>
                                                                                     handleFileSelect(
                                                                                          section.field,
                                                                                          file
                                                                                     )
                                                                                }
                                                                                accept="image/*,.pdf"
                                                                           >
                                                                                {(
                                                                                     props
                                                                                ) => (
                                                                                     <Paper
                                                                                          {...props}
                                                                                          p="xl"
                                                                                          radius="md"
                                                                                          style={{
                                                                                               border: "2px dashed #D0D0D0",
                                                                                               background:
                                                                                                    "#FAFAFA",
                                                                                               cursor: "pointer",
                                                                                               transition:
                                                                                                    "all 0.2s",
                                                                                          }}
                                                                                          className="hover:border-[#679369] hover:bg-gray-50"
                                                                                     >
                                                                                          <Stack
                                                                                               align="center"
                                                                                               gap="md"
                                                                                          >
                                                                                               {currentField && currentField.uploading ? (
                                                                                                    <>
                                                                                                         <div className="animate-spin">
                                                                                                              <IconUpload
                                                                                                                   size={
                                                                                                                        48
                                                                                                                   }
                                                                                                                   style={{
                                                                                                                        color: "#2C2C2C",
                                                                                                                   }}
                                                                                                              />
                                                                                                         </div>
                                                                                                         <Text
                                                                                                              size="sm"
                                                                                                              fw={
                                                                                                                   600
                                                                                                              }
                                                                                                              style={{
                                                                                                                   color: "#2C2C2C",
                                                                                                              }}
                                                                                                         >
                                                                                                              Uploading...
                                                                                                         </Text>
                                                                                                    </>
                                                                                               ) : (
                                                                                                    <>
                                                                                                         <IconFileText
                                                                                                              size={
                                                                                                                   48
                                                                                                              }
                                                                                                              style={{
                                                                                                                   color: "#9CA3AF",
                                                                                                              }}
                                                                                                         />
                                                                                                         <div className="text-center">
                                                                                                              <Text
                                                                                                                   size="sm"
                                                                                                                   fw={
                                                                                                                        600
                                                                                                                   }
                                                                                                                   style={{
                                                                                                                        color: "#2C2C2C",
                                                                                                                   }}
                                                                                                              >
                                                                                                                   Click
                                                                                                                   to
                                                                                                                   upload
                                                                                                                   or
                                                                                                                   drag
                                                                                                                   and
                                                                                                                   drop
                                                                                                              </Text>
                                                                                                              <Text
                                                                                                                   size="xs"
                                                                                                                   c="dimmed"
                                                                                                                   mt="xs"
                                                                                                              >
                                                                                                                   PNG,
                                                                                                                   JPG,
                                                                                                                   PDF
                                                                                                                   up
                                                                                                                   to
                                                                                                                   10MB
                                                                                                              </Text>
                                                                                                         </div>
                                                                                                    </>
                                                                                               )}
                                                                                          </Stack>
                                                                                     </Paper>
                                                                                )}
                                                                           </FileButton>
                                                                      )}
                                                                 </div>
                                                            )}
                                                       </>
                                                  )}

                                                  {/* Confirmation Checkbox and Submit Button (only interactive on active section) */}
                                                  {!isSubmittedOrVerified && (
                                                       <Stack gap="md" mt="xl">
                                                            <Checkbox
                                                                 label="I confirm that the document uploaded is correct and accurate"
                                                                 checked={
                                                                      confirmations[
                                                                           `step${activeStep}` as keyof typeof confirmations
                                                                      ]
                                                                 }
                                                                 onChange={(
                                                                      e
                                                                 ) => {
                                                                      const stepKey =
                                                                           `step${activeStep}` as keyof typeof confirmations;
                                                                      setConfirmations(
                                                                           (
                                                                                prev
                                                                           ) => ({
                                                                                ...prev,
                                                                                [stepKey]:
                                                                                     e
                                                                                          ?.currentTarget
                                                                                          ?.checked,
                                                                           })
                                                                      );
                                                                 }}
                                                                 styles={{
                                                                      label: {
                                                                           fontSize:
                                                                                "14px",
                                                                           fontWeight: 500,
                                                                      },
                                                                 }}
                                                            />

                                                            <Group justify="flex-end">
                                                                 <Button
                                                                      onClick={
                                                                           handleNextStep
                                                                      }
                                                                      disabled={
                                                                           !canProceed() ||
                                                                           submitting ||
                                                                           !canUploadStep(
                                                                                activeStep
                                                                           )
                                                                      }
                                                                      loading={
                                                                           submitting
                                                                      }
                                                                      style={{
                                                                           background:
                                                                                "#679369",
                                                                           color: "white",
                                                                           opacity:
                                                                                !canProceed() ||
                                                                                submitting ||
                                                                                !canUploadStep(
                                                                                     activeStep
                                                                                )
                                                                                     ? 0.5
                                                                                     : 1,
                                                                      }}
                                                                      className="hover:opacity-90"
                                                                      size={
                                                                           activeStep <
                                                                           uploadSections.length -
                                                                                1
                                                                                ? "md"
                                                                                : "lg"
                                                                      }
                                                                 >
                                                                      Submit{" "}
                                                                      {
                                                                           section.title
                                                                      }
                                                                 </Button>
                                                            </Group>
                                                       </Stack>
                                                  )}
                                             </Stack>
                                        </Paper>
                                   </motion.div>
                              );
                         })}
                    </Stack>
               </Stack>
          </Container>
     );
}
